"use client"

import { useState } from "react"
import { Link } from "@tanstack/react-router"
import { cn } from "@shared/lib/utils"
import { Button } from "@/shared/components/ui/button"
import { Users, Menu, X, FileSignature, ChevronLeft, Receipt, Settings } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useIsDesktop } from "@shared/hooks/use-media-query"
import { usePermissions } from "@/shared/hooks/use-permissions"
import { useLocalStorage } from "@shared/hooks/use-local-storage"
import { useConsistentNavigation } from "@/shared/hooks/use-consistent-navigation"
import { ADMIN_ROUTES } from "@admin/constants/routes"

interface SidebarProps {
  className?: string
}

export function Sidebar({ className }: SidebarProps) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useLocalStorage("sidebar-expanded", true)
  const isDesktop = useIsDesktop()
  const { canViewTransactions, canViewReports, hasPermission, userRole } = usePermissions()
  const { preloadRoute } = useConsistentNavigation()

  // Preload billing route for faster navigation
  const handleBillingHover = () => {
    if (canViewReports) {
      preloadRoute(ADMIN_ROUTES.BILLING)
    }
  }

  const toggleMobileSidebar = () => {
    setMobileOpen(!mobileOpen)
  }

  const toggleSidebar = () => {
    setIsExpanded(!isExpanded)
  }

  const navigation = [
    // Dashboard removed
    canViewTransactions ? { 
      name: "Transactions", 
      href: ADMIN_ROUTES.TRANSACTIONS, 
      icon: FileSignature,
      description: "View and manage application transactions"
    } : null,
    hasPermission('VIEW_USER') ? { 
      name: "Users", 
      href: ADMIN_ROUTES.USERS, 
      icon: Users,
      description: "Manage user accounts and permissions"
    } : null,
    canViewReports ? { 
      name: "Reports & Billing", 
      href: ADMIN_ROUTES.BILLING, 
      icon: Receipt,
      description: "View billing reports and financial data"
    } : null,
    hasPermission('VIEW_ORGANIZATION') ? { 
      name: "F1 Settings", 
      href: ADMIN_ROUTES.F1_SETTINGS, 
      icon: Settings,
      description: "Configure FrontierOne system settings"
    } : null,
  ].filter(Boolean) as Array<{ name: string; href: string; icon: LucideIcon; description: string }>

  // Show permission status for viewers
  const showPermissionStatus = userRole === 'Viewer' && navigation.length <= 1

  const sidebarContent = (
    <>
      {/* Header */}
      <div className="flex h-16 items-center px-3 py-6">
        {/* Close button for non-desktop (overlay) only */}
        {!isDesktop && (
          <Button variant="ghost" size="icon" onClick={toggleMobileSidebar} className="ml-2">
            <X className="h-5 w-5" />
            <span className="sr-only">Close sidebar</span>
          </Button>
        )}
      </div>

      {/* Navigation Links */}
      <div className="space-y-1 px-3 py-2">
        {navigation.map((item) => (
          <Link
            key={item.name}
            to={item.href}
            className={cn(
              "flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              !isExpanded && isDesktop && "justify-center",
            )}
            activeProps={{
              className: "bg-accent text-accent-foreground",
            }}
            title={!isExpanded && isDesktop ? `${item.name} - ${item.description}` : undefined}
            onMouseEnter={item.href === '/billing' ? handleBillingHover : undefined}
          >
            <item.icon className={cn("h-5 w-5 flex-shrink-0", (isExpanded || !isDesktop) && "mr-2")} />
            {(isExpanded || !isDesktop) && <span className="whitespace-nowrap">{item.name}</span>}
          </Link>
        ))}
        
        {/* Permission Status for Viewers */}
        {showPermissionStatus && (isExpanded || !isDesktop) && (
          <div className="mt-4 px-3 py-2">
            <div className="text-xs text-muted-foreground mb-2">Permission Status</div>
            <div className="space-y-1">
              <div className={cn(
                "flex items-center text-xs",
                canViewTransactions ? "text-green-600" : "text-gray-400"
              )}>
                <div className={cn(
                  "w-2 h-2 rounded-full mr-2",
                  canViewTransactions ? "bg-green-500" : "bg-gray-300"
                )} />
                {canViewTransactions ? "? View Transactions" : "? View Transactions"}
              </div>
              <div className={cn(
                "flex items-center text-xs",
                canViewReports ? "text-green-600" : "text-gray-400"
              )}>
                <div className={cn(
                  "w-2 h-2 rounded-full mr-2",
                  canViewReports ? "bg-green-500" : "bg-gray-300"
                )} />
                {canViewReports ? "? View Reports" : "? View Reports"}
              </div>
            </div>
            {navigation.length === 1 && (
              <div className="mt-2 text-xs text-amber-600 bg-amber-50 p-2 rounded">
                Limited access: You can only view {navigation[0].name.toLowerCase()}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )

  return (
    <>
      {/* Menu button when not desktop (tablet/mobile) */}
      {!isDesktop && (
        <Button
          variant="outline"
          size="icon"
          onClick={toggleMobileSidebar}
          className="fixed left-4 top-2 z-40 bg-transparent lg:hidden"
        >
          <Menu className="h-5 w-5" />
          <span className="sr-only">Open sidebar</span>
        </Button>
      )}

      {/* Overlay sidebar (tablet + mobile) */}
      {!isDesktop && (
        <div
          className={cn(
            "fixed inset-0 z-50 transition-all duration-300 ease-out",
            mobileOpen
              ? "bg-background/80 backdrop-blur-sm opacity-100"
              : "pointer-events-none opacity-0 bg-background/0",
          )}
          style={{
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div
            className={cn(
              "fixed inset-y-0 left-0 z-50 h-full w-3/4 max-w-xs bg-background shadow-2xl",
              "transition-all duration-400 ease-out",
              mobileOpen ? "translate-x-0" : "-translate-x-full",
            )}
            style={{
              transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease-out",
              boxShadow: mobileOpen
                ? "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)"
                : "0 0 0 0 rgba(0, 0, 0, 0)",
            }}
          >
            <div
              className={cn(
                "transition-all duration-300 ease-out",
                mobileOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
              )}
              style={{
                transitionDelay: mobileOpen ? "200ms" : "0ms",
              }}
            >
              {sidebarContent}
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      {isDesktop && (
        <div
          className={cn(
            "sticky top-14 self-start flex flex-col border-r bg-background transition-all duration-500 ease-out",
            isExpanded ? "w-64" : "w-16",
            "h-[calc(100vh-3.5rem)]",
            className,
          )}
          style={{
            transition: "width 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div className="flex flex-col overflow-hidden flex-1 min-h-0">
            {/* Navigation Links - sticky below sidebar header when sidebar scrolls */}
            <div className="sticky z-10 flex-shrink-0 space-y-1 px-3 py-2 bg-background">
              {navigation.map((item, index) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "flex items-center rounded-md px-3 py-2 text-sm font-medium transition-all duration-300 ease-out hover:bg-accent hover:text-accent-foreground",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    !isExpanded && "justify-center",
                  )}
                  style={{
                    transitionDelay: isExpanded ? `${index * 50}ms` : "0ms",
                  }}
                  activeProps={{
                    className: "bg-accent text-accent-foreground",
                  }}
                  title={!isExpanded ? `${item.name} - ${item.description}` : undefined}
                  onMouseEnter={item.href === '/billing' ? handleBillingHover : undefined}
                >
                  <item.icon
                    className={cn(
                      "h-5 w-5 flex-shrink-0 transition-all duration-300 ease-out",
                      isExpanded && "mr-2",
                    )}
                  />
                  <span
                    className={cn(
                      "whitespace-nowrap transition-all duration-300 ease-out",
                      isExpanded
                        ? "opacity-100 translate-x-0"
                        : "opacity-0 -translate-x-2 w-0 overflow-hidden",
                    )}
                    style={{
                      transitionDelay: isExpanded ? "200ms" : "0ms",
                    }}
                  >
                    {item.name}
                  </span>
                </Link>
              ))}
              
              {/* Permission Status for Viewers */}
              {showPermissionStatus && isExpanded && (
                <div className="mt-4 px-3 py-2">
                  <div className="text-xs text-muted-foreground mb-2">Permission Status</div>
                  <div className="space-y-1">
                    <div className={cn(
                      "flex items-center text-xs",
                      canViewTransactions ? "text-green-600" : "text-gray-400"
                    )}>
                      <div className={cn(
                        "w-2 h-2 rounded-full mr-2",
                        canViewTransactions ? "bg-green-500" : "bg-gray-300"
                      )} />
                      {canViewTransactions ? "? View Transactions" : "? View Transactions"}
                    </div>
                    <div className={cn(
                      "flex items-center text-xs",
                      canViewReports ? "text-green-600" : "text-gray-400"
                    )}>
                      <div className={cn(
                        "w-2 h-2 rounded-full mr-2",
                        canViewReports ? "bg-green-500" : "bg-gray-300"
                      )} />
                      {canViewReports ? "? View Reports" : "? View Reports"}
                    </div>
                  </div>
                  {navigation.length === 1 && (
                    <div className="mt-2 text-xs text-amber-600 bg-amber-50 p-2 rounded">
                      Limited access: You can only view {navigation[0].name.toLowerCase()}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Desktop toggle button - positioned absolutely with smooth animation */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className={cn(
              "absolute top-3 z-10 flex-shrink-0 w-6 h-6 rounded-full bg-white",
              "hover:bg-white hover:text-accent-foreground hover:scale-110",
              "duration-500 ease-out",
              "shadow-md hover:shadow-lg",
              isExpanded ? "left-[243px] opacity-100" : "left-[63px] -translate-x-1/2 opacity-90",
            )}
            style={{
              transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
              transitionProperty: "right, left, transform, opacity, background-color, box-shadow, scale",
            }}
            title={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
          >
            <div
              className="transition-transform duration-300 ease-out"
              style={{
                transform: isExpanded ? "rotate(0deg)" : "rotate(180deg)",
              }}
            >
              <ChevronLeft className="h-4 w-4" />
            </div>
            <span className="sr-only">{isExpanded ? "Collapse sidebar" : "Expand sidebar"}</span>
          </Button>
        </div>
      )}
    </>
  )
}
